import type { L1Keys, MenuJsonType, MenuType } from './Menu'
import CurvedCorner from './CurvedCorner'
import { NavigationModule } from './Modules/Modules'
import { hasItems, isExternal } from './navUtils'
import type { LinkCompType } from './navUtils'

function L4Item({ L4, LinkComp }: { L4: MenuType; LinkComp: LinkCompType }) {
  return (
    <li className="group/l4 flex">
      <LinkComp
        href={L4.url ?? '#'}
        className="nav-level-3 flex items-center py-1"
      >
        <div className="flex h-7 w-7 items-center justify-center rounded bg-[var(--nav-accent-10)] text-[var(--nav-accent-text)] transition-all duration-300 ease-in-out group-hover/l4:bg-[var(--nav-accent-30)]">
          <span className="nav-chevron -rotate-90" />
        </div>
        <span className="block px-2 leading-tight">{L4.label}</span>
        {isExternal(L4.url) && (
          <span className="nav-external-link text-black" />
        )}
      </LinkComp>
    </li>
  )
}

export function DesktopNav({
  menuJson,
  L1Key,
  LinkComp,
}: {
  menuJson: MenuJsonType
  L1Key: L1Keys
  LinkComp: LinkCompType
}) {
  return (
    <>
      {/* L1 navigation - desktop */}
      {Object.keys(menuJson).map((key) => {
        const L1 = menuJson[key]
        return (
          <li key={key} className="hidden lg:!flex" data-l1-key={key}>
            <LinkComp
              className={`nav-level-1 relative flex items-center px-4 text-black`}
              href={L1?.url ?? '#'}
            >
              <div
                data-l1-tab-bg
                className={`absolute bottom-0 left-0 h-[80%] w-full rounded-t-3xl bg-[var(--nav-accent)] fill-[var(--nav-accent)] opacity-0`}
              >
                <CurvedCorner
                  classes={{
                    fill: '',
                    size: 'w-5 h-5',
                    container: 'absolute bottom-0 right-full',
                  }}
                />
                <CurvedCorner
                  classes={{
                    fill: '',
                    size: 'w-5 h-5',
                    container: 'absolute bottom-0 left-full -scale-x-100',
                  }}
                />
              </div>
              <span className="relative">{L1?.label}</span>
            </LinkComp>
            {/* white underlay */}
            <div className="absolute left-0 top-0 -z-10 h-full w-full bg-white"></div>
            {/* L2 navigation - only rendered for the active L1 */}
            {key === L1Key && (
              <div
                data-l2-nav
                className={`absolute left-0 top-[100%] -z-20 hidden w-full bg-[var(--nav-accent)] text-white transition-all duration-300`}
              >
                <div className="mx-auto h-full w-[1440px] max-w-full px-4 lg:px-8">
                  <div className="flex h-[40px] justify-start">
                    <ul className="-mx-4 flex">
                      {L1?.items?.map((L2: MenuType) => (
                        <li key={L2.label} className="group">
                          <LinkComp
                            href={L2.url ?? '#'}
                            className="nav-level-2 flex h-full items-center"
                          >
                            <div className="duration-250 flex h-[80%] items-center gap-1 rounded-md px-4 transition-all group-hover:bg-white/25">
                              {L2.label}
                              {hasItems(L2) && (
                                <span className="nav-chevron transition-transform duration-300 ease-in-out group-hover:-rotate-90" />
                              )}
                            </div>
                          </LinkComp>
                          {/* L3 navigation */}
                          {hasItems(L2) && (
                            <div className="absolute left-0 top-full hidden w-full overflow-hidden border-b border-[var(--nav-accent)] bg-white text-black group-hover:!block">
                              <div className="mx-auto h-full w-[1440px] max-w-full px-4 lg:px-8">
                                <div className="relative -left-4 flex w-full animate-[slideInX_250ms_ease-in_forwards] opacity-25">
                                  <div className="relative flex w-full">
                                    {/* L3 navigation - headings */}
                                    {Array.isArray(L2.items) &&
                                      L2.items.map(
                                        (L3: MenuType, L3Index: number) => {
                                          const twoCol = L3.columns === 2
                                          const splitAt = Math.ceil(
                                            (L3.items?.length ?? 0) / 2,
                                          )
                                          const colGroups = [
                                            L3.items?.slice(0, splitAt) ?? [],
                                            L3.items?.slice(splitAt) ?? [],
                                          ]

                                          return (
                                            <ul
                                              key={`${L3.label ?? ''}_${String(L3Index)}`}
                                              className={`min-w-[200px] ${twoCol ? 'w-[500px]' : 'w-[300px]'} shrink-999 -mb-1 flex flex-col py-8 pr-6`}
                                            >
                                              <li
                                                className="font-normal text-[var(--nav-accent-text)]"
                                                data-l3-heading
                                              >
                                                {L3.label}
                                              </li>
                                              {/* L4 navigation - items */}
                                              {hasItems(L3) &&
                                                (twoCol ? (
                                                  <li className="flex">
                                                    {colGroups.map(
                                                      (col, colIdx) => (
                                                        <ul
                                                          key={colIdx}
                                                          className="flex flex-1 flex-col"
                                                        >
                                                          {col.map(
                                                            (L4: MenuType) => (
                                                              <L4Item
                                                                key={L4.label}
                                                                L4={L4}
                                                                LinkComp={
                                                                  LinkComp
                                                                }
                                                              />
                                                            ),
                                                          )}
                                                        </ul>
                                                      ),
                                                    )}
                                                  </li>
                                                ) : (
                                                  L3.items?.map(
                                                    (L4: MenuType) => (
                                                      <L4Item
                                                        key={L4.label}
                                                        L4={L4}
                                                        LinkComp={LinkComp}
                                                      />
                                                    ),
                                                  )
                                                ))}
                                            </ul>
                                          )
                                        },
                                      )}
                                    {L2.component && (
                                      <>
                                        <div className="grow"></div>
                                        <NavigationModule
                                          id={L2.component.id}
                                          LinkComp={LinkComp}
                                        />
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </li>
        )
      })}
    </>
  )
}
